import React, { useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  BarGraphIcon1,
  BarGraphIcon2,
  BarGraphIcon3,
  HelpCircleIcon,
  TrendingUpIcon,
  TrendingDownIcon,
} from "../../../commonUI/Icons/SvgIcon";
import TooltipIcon from "../../../commonUI/Tooltip/TooltipIcon";
import KPICardsGrid from "../../../commonUI/Cards/KPICardsGrid";

const INTERVAL_OPTIONS = ["Daily", "Weekly", "Monthly", "Yearly"];

const METRIC_OPTIONS = [
  { value: "active-users", label: "Active users" },
  { value: "sessions", label: "Sessions" },
  { value: "first-opens", label: "First opens" },
];

const APP_CHART_DATA = [
  { month: "22 Aug", ios: 310, android: 165 },
  { month: "24 Aug", ios: 300, android: 165 },
  { month: "25 Aug", ios: 370, android: 215 },
  { month: "26 Aug", ios: 385, android: 215 },
  { month: "27 Aug", ios: 350, android: 200 },
  { month: "28 Aug", ios: 375, android: 195 },
  { month: "29 Aug", ios: 285, android: 170 },
  { month: "30 Aug", ios: 295, android: 175 },
  { month: "1 Sep", ios: 390, android: 255 },
  { month: "2 Sep", ios: 330, android: 210 },
  { month: "3 Sep", ios: 395, android: 265 },
  { month: "4 Sep", ios: 315, android: 190 },
  { month: "5 Sep", ios: 295, android: 175 },
  { month: "6 Sep", ios: 300, android: 165 },
  { month: "7 Sep", ios: 305, android: 180 },
  { month: "8 Sep", ios: 305, android: 175 },
  { month: "9 Sep", ios: 435, android: 255 },
  { month: "10 Sep", ios: 310, android: 185 },
  { month: "11 Sep", ios: 440, android: 295 },
  { month: "12 Sep", ios: 315, android: 200 },
  { month: "13 Sep", ios: 310, android: 180 },
  { month: "14 Sep", ios: 415, android: 265 },
  { month: "15 Sep", ios: 400, android: 260 },
  { month: "16 Sep", ios: 400, android: 270 },
  { month: "17 Sep", ios: 390, android: 245 },
  { month: "18 Sep", ios: 395, android: 255 },
  { month: "19 Sep", ios: 305, android: 195 },
  { month: "20 Sep", ios: 330, android: 195 },
];

const PLATFORM_ACTIVITY = [
  {
    platform: "iOS app",
    activeUsers: 5677,
    activeUsersChange: -0.7,
    sessions: 22648,
    sessionsChange: 3.2,
    firstOpens: 800,
    firstOpensChange: -4.2,
    engagementRate: "71.9%",
    engagementRateChange: -0.1,
  },
  {
    platform: "Android app",
    activeUsers: 3351,
    activeUsersChange: -1.8,
    sessions: 11662,
    sessionsChange: -4.8,
    firstOpens: 419,
    firstOpensChange: -8.9,
    engagementRate: "72.2%",
    engagementRateChange: -0.2,
  },
];

const IOS_VERSIONS = [
  { version: "2.6.38", releaseDate: "Released 24 Aug 2026", activeUsers: 3332, share: "58.7%" },
  { version: "2.6.37", releaseDate: "Released 13 Jul 2026", activeUsers: 1571, share: "27.7%" },
  { version: "2.6.36", releaseDate: "Released 18 May 2026", activeUsers: 563, share: "9.9%" },
  { version: "2.6.35", releaseDate: "Release date unavailable", activeUsers: 211, share: "3.7%" },
];

const ANDROID_VERSIONS = [
  { version: "2.6.38", releaseDate: "Released 26 Aug 2026", activeUsers: 1879, share: "56.1%" },
  { version: "2.6.37", releaseDate: "Released 15 Jul 2026", activeUsers: 1019, share: "30.4%" },
  { version: "2.6.36", releaseDate: "Released 20 May 2026", activeUsers: 311, share: "9.3%" },
  { version: "2.6.35", releaseDate: "Release date unavailable", activeUsers: 142, share: "4.2%" },
];

const TrendBadge = ({ change, unit = "%" }) => {
  const isPositive = change >= 0;
  return (
    <div className={`analytics-trend ${isPositive ? "analytics-trend--positive" : "analytics-trend--negative"}`}>
      {isPositive ? (
        <TrendingUpIcon width="12" height="12" />
      ) : (
        <TrendingDownIcon width="12" height="12" />
      )}
      {isPositive ? "+" : ""}
      {change.toFixed(1)}{unit}
    </div>
  );
};

const AppSection = ({ snapshot }) => {
  const [chartType, setChartType] = useState("bar");
  const [selectedMetric, setSelectedMetric] = useState("active-users");
  const [selectedInterval, setSelectedInterval] = useState("Daily");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIndicator = (key) =>
    sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕";

  const commonChartProps = {
    data: APP_CHART_DATA,
    margin: { top: 10, right: 30, left: 0, bottom: 10 },
  };

  const commonAxis = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
      <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: "12px" }} />
      <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
      <Tooltip
        contentStyle={{
          backgroundColor: "#fff",
          border: "1px solid #e5e7eb",
          fontSize: "13px",
        }}
      />
    </>
  );

  const renderChart = () => {
    if (chartType === "line") {
      return (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart {...commonChartProps}>
            {commonAxis}
            <Line type="monotone" dataKey="ios" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} name="iOS" />
            <Line type="monotone" dataKey="android" stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Android" />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === "area") {
      return (
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart {...commonChartProps}>
            {commonAxis}
            <defs>
              <linearGradient id="colorIOS" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorAndroid" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="ios" stroke="#3b82f6" fill="url(#colorIOS)" strokeWidth={2} name="iOS" />
            <Area type="monotone" dataKey="android" stroke="#0d9488" fill="url(#colorAndroid)" strokeWidth={2} name="Android" />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    // Default: stacked bar chart
    return (
      <ResponsiveContainer width="100%" height={320}>
        <BarChart {...commonChartProps}>
          {commonAxis}
          <Bar dataKey="ios" stackId="platform" fill="#3b82f6" name="iOS" />
          <Bar dataKey="android" stackId="platform" fill="#0d9488" name="Android" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const kpiData = [
    {
      label: "Active users",
      tooltipTitle: "Distinct active users across iOS and Android apps in the selected period.",
      value: "9,028",
      changePercent: "-1.1%",
      changeType: "negative",
    },
    {
      label: "Sessions",
      tooltipTitle: "Total sessions across iOS and Android apps in the selected period.",
      value: "34,310",
      changePercent: "+0.3%",
      changeType: "positive",
    },
    {
      label: "First opens",
      tooltipTitle: "Number of times the app was opened for the first time after installation.",
      value: "1,219",
      changePercent: "-5.9%",
      changeType: "negative",
    },
  ];

  return (
    <>
      {/* KPI Cards */}
      <KPICardsGrid cards={kpiData} />

      {/* App Activity Chart */}
      <div className="analytics-chart-box">
        <div className="analytics-chart-header">
          <div className="analytics-chart-title-header">
            <div className="analytics-chart-title-left">
              <span className="analytics-chart-title">App activity</span>
              <TooltipIcon
                title="Distinct active users per interval and platform. Someone using both apps is counted in a stacked total."
                iconWidth="16"
                iconHeight="16"
                inline={true}
              />
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="analytics-metric-select analytics-metric-select--sm"
              >
                {METRIC_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="analytics-chart-title-right">
              <select
                value={selectedInterval}
                onChange={(e) => setSelectedInterval(e.target.value)}
                className="analytics-interval-select"
              >
                {INTERVAL_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <div className="analytics-chart-type-buttons">
                <button
                  onClick={() => setChartType("bar")}
                  className={`analytics-chart-type-btn ${chartType === "bar" ? "active" : ""}`}
                  title="Bar Chart"
                >
                  <BarGraphIcon1 />
                </button>
                <button
                  onClick={() => setChartType("area")}
                  className={`analytics-chart-type-btn ${chartType === "area" ? "active" : ""}`}
                  title="Area Chart"
                >
                  <BarGraphIcon2 />
                </button>
                <button
                  onClick={() => setChartType("line")}
                  className={`analytics-chart-type-btn ${chartType === "line" ? "active" : ""}`}
                  title="Line Chart"
                >
                  <BarGraphIcon3 />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="analytics-chart-content">
          {renderChart()}
        </div>
        <div className="analytics-chart-legend">
          <span className="analytics-chart-legend__item">
            <span className="analytics-chart-legend__dot" style={{ backgroundColor: "#3b82f6" }} />
            iOS
          </span>
          <span className="analytics-chart-legend__item">
            <span className="analytics-chart-legend__dot" style={{ backgroundColor: "#0d9488" }} />
            Android
          </span>
        </div>
      </div>

      {/* Platform Activity Table */}
      <div className="analytics-section-table">
        <div className="analytics-section-table__header">
          <h3 className="analytics-section-table__title">Platform activity</h3>
        </div>
        <hr className="analytics-section-table__divider" />
        <div className="analytics-section-table__wrapper">
          <table className="analytics-section-table__table">
            <thead>
              <tr className="analytics-section-table__head-row">
                <th
                  className="analytics-section-table__head-cell"
                  onClick={() => handleSort("platform")}
                >
                  Platform {getSortIndicator("platform")}
                </th>
                <th
                  className="analytics-section-table__head-cell analytics-section-table__head-cell--right"
                  onClick={() => handleSort("activeUsers")}
                >
                  Active users{" "}
                  <TooltipIcon
                    title="Distinct active users across iOS and Android apps in the selected period."
                    iconWidth="14"
                    iconHeight="14"
                    inline={true}
                  />{" "}
                  {getSortIndicator("activeUsers")}
                </th>
                <th
                  className="analytics-section-table__head-cell analytics-section-table__head-cell--right"
                  onClick={() => handleSort("sessions")}
                >
                  Sessions{" "}
                  <TooltipIcon
                    title="Total sessions across iOS and Android apps in the selected period."
                    iconWidth="14"
                    iconHeight="14"
                    inline={true}
                  />{" "}
                  {getSortIndicator("sessions")}
                </th>
                <th
                  className="analytics-section-table__head-cell analytics-section-table__head-cell--right"
                  onClick={() => handleSort("firstOpens")}
                >
                  First opens{" "}
                  <TooltipIcon
                    title="Number of times the app was opened for the first time after installation."
                    iconWidth="14"
                    iconHeight="14"
                    inline={true}
                  />{" "}
                  {getSortIndicator("firstOpens")}
                </th>
                <th
                  className="analytics-section-table__head-cell analytics-section-table__head-cell--right"
                  onClick={() => handleSort("engagementRate")}
                >
                  Engagement rate{" "}
                  <TooltipIcon
                    title="Percentage of active users who had at least one engaged session."
                    iconWidth="14"
                    iconHeight="14"
                    inline={true}
                  />{" "}
                  {getSortIndicator("engagementRate")}
                </th>
              </tr>
            </thead>
            <tbody>
              {PLATFORM_ACTIVITY.map((row, idx) => (
                <tr key={idx} className="analytics-section-table__body-row">
                  <td className="analytics-section-table__body-cell">{row.platform}</td>
                  <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                    <div>{row.activeUsers.toLocaleString()}</div>
                    <TrendBadge change={row.activeUsersChange} />
                  </td>
                  <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                    <div>{row.sessions.toLocaleString()}</div>
                    <TrendBadge change={row.sessionsChange} />
                  </td>
                  <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                    <div>{row.firstOpens.toLocaleString()}</div>
                    <TrendBadge change={row.firstOpensChange} />
                  </td>
                  <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                    <div>{row.engagementRate}</div>
                    <TrendBadge change={row.engagementRateChange} unit=" pp" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* iOS + Android Version Usage */}
      <div className="analytics-tables-two-col">
        {/* iOS Version Usage */}
        <div className="analytics-section-table">
          <div className="analytics-section-table__header">
            <h3 className="analytics-section-table__title">iOS version usage</h3>
            <TooltipIcon
              title="Usage distribution across different iOS app versions."
              iconWidth="16"
              iconHeight="16"
              inline={true}
            />
          </div>
          <hr className="analytics-section-table__divider" />
          <div className="analytics-section-table__wrapper">
            <table className="analytics-section-table__table">
              <thead>
                <tr className="analytics-section-table__head-row">
                  <th className="analytics-section-table__head-cell">
                    Version{" "}
                    <TooltipIcon
                      title="App version number."
                      iconWidth="14"
                      iconHeight="14"
                      inline={true}
                    />
                  </th>
                  <th className="analytics-section-table__head-cell analytics-section-table__head-cell--right">
                    Active users{" "}
                    <TooltipIcon
                      title="Distinct active users on this version."
                      iconWidth="14"
                      iconHeight="14"
                      inline={true}
                    />
                  </th>
                  <th className="analytics-section-table__head-cell analytics-section-table__head-cell--right">
                    Share{" "}
                    <TooltipIcon
                      title="Percentage of total active users using this version."
                      iconWidth="14"
                      iconHeight="14"
                      inline={true}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {IOS_VERSIONS.map((row, idx) => (
                  <tr key={idx} className="analytics-section-table__body-row">
                    <td className="analytics-section-table__body-cell">
                      <div className="analytics-section-table__version">{row.version}</div>
                      <div className="analytics-section-table__version-date">{row.releaseDate}</div>
                    </td>
                    <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                      {row.activeUsers.toLocaleString()}
                    </td>
                    <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                      {row.share}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Android Version Usage */}
        <div className="analytics-section-table">
          <div className="analytics-section-table__header">
            <h3 className="analytics-section-table__title">Android version usage</h3>
            <TooltipIcon
              title="Usage distribution across different Android app versions."
              iconWidth="16"
              iconHeight="16"
              inline={true}
            />
          </div>
          <hr className="analytics-section-table__divider" />
          <div className="analytics-section-table__wrapper">
            <table className="analytics-section-table__table">
              <thead>
                <tr className="analytics-section-table__head-row">
                  <th className="analytics-section-table__head-cell">
                    Version{" "}
                    <TooltipIcon
                      title="App version number."
                      iconWidth="14"
                      iconHeight="14"
                      inline={true}
                    />
                  </th>
                  <th className="analytics-section-table__head-cell analytics-section-table__head-cell--right">
                    Active users{" "}
                    <TooltipIcon
                      title="Distinct active users on this version."
                      iconWidth="14"
                      iconHeight="14"
                      inline={true}
                    />
                  </th>
                  <th className="analytics-section-table__head-cell analytics-section-table__head-cell--right">
                    Share{" "}
                    <TooltipIcon
                      title="Percentage of total active users using this version."
                      iconWidth="14"
                      iconHeight="14"
                      inline={true}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {ANDROID_VERSIONS.map((row, idx) => (
                  <tr key={idx} className="analytics-section-table__body-row">
                    <td className="analytics-section-table__body-cell">
                      <div className="analytics-section-table__version">{row.version}</div>
                      <div className="analytics-section-table__version-date">{row.releaseDate}</div>
                    </td>
                    <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                      {row.activeUsers.toLocaleString()}
                    </td>
                    <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                      {row.share}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppSection;
